import { useState, useEffect, useCallback } from 'react';
import { rpc } from '@/api/client';
import { LoadingSpinner } from '@/components/common/LoadingSpinner';
import { ErrorState } from '@/components/common/ErrorState';
import { useToast } from '@/context/ToastContext';
import { useI18n } from '@/context/I18nContext';
import { Pencil, Wrench } from 'lucide-react';
import {
  SetupForm,
  isSetupEmpty,
  type SetupData,
  type InventoryItem,
  type SetupInventory,
} from './SetupForm';

export function SetupPage() {
  const { t } = useI18n();
  const { showToast } = useToast();
  const [setup, setSetup] = useState<SetupData | null>(null);
  const [data, setData] = useState<SetupData>({});
  const [readOnly, setReadOnly] = useState(true);
  const [saving, setSaving] = useState(false);
  const [inventory, setInventory] = useState<SetupInventory | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    async function load() {
      setLoading(true);
      setError(null);
      try {
        const [setupData, chassis, engines, tyres, carbs, axlesData, hubs, sparkPlugs] = await Promise.all([
          rpc<SetupData>('GetKartSetup'),
          rpc<InventoryItem[]>('ListChassis'),
          rpc<InventoryItem[]>('ListEngines'),
          rpc<InventoryItem[]>('ListTyres'),
          rpc<InventoryItem[]>('ListCarburetors'),
          rpc<InventoryItem[]>('ListAxles'),
          rpc<InventoryItem[]>('ListHubs'),
          rpc<InventoryItem[]>('ListSparkPlugs'),
        ]);
        if (cancelled) return;
        setSetup(setupData);
        setData({ ...setupData });
        setInventory({
          chassis: chassis || [],
          engines: engines || [],
          tyres: tyres || [],
          carburetors: carbs || [],
          axles: axlesData || [],
          hubs: hubs || [],
          sparkPlugs: sparkPlugs || [],
        });
      } catch (e: any) {
        if (!cancelled) setError(e.message || t('setup.loadFailed'));
      } finally {
        if (!cancelled) setLoading(false);
      }
    }
    load();
    return () => { cancelled = true; };
  }, [t]);

  const update = useCallback((partial: Partial<SetupData>) => {
    setData(prev => ({ ...prev, ...partial }));
  }, []);

  const handleStartEdit = useCallback(() => {
    setData({ ...(setup ?? {}) });
    setReadOnly(false);
  }, [setup]);

  const handleCancel = useCallback(() => {
    setData({ ...(setup ?? {}) });
    setReadOnly(true);
  }, [setup]);

  const handleSave = async () => {
    setSaving(true);
    try {
      await rpc('UpsertKartSetup', {
        ...data,
      } as unknown as Record<string, unknown>);
      setSetup({ ...data });
      setReadOnly(true);
      showToast(t('setup.saved'));
    } catch {
      showToast(t('setup.saveFailed'), 'error');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div>
      <div className="flex items-center gap-3 mb-8">
        <div className="w-9 h-9 rounded-lg bg-accent/10 border border-accent/20 flex items-center justify-center">
          <Wrench size={18} className="text-accent" />
        </div>
        <div>
          <h1 className="text-xl font-semibold tracking-tight">{t('setup.pageTitle')}</h1>
          <p className="text-xs text-text-muted mt-0.5">{t('setup.pageSubtitle')}</p>
        </div>
      </div>

      {loading ? (
        <LoadingSpinner />
      ) : error ? (
        <ErrorState message={error} />
      ) : (
        <>
          <div className="flex justify-end gap-2 mb-4">
            {readOnly ? (
              <button
                type="button"
                onClick={handleStartEdit}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-medium bg-accent text-white
                  hover:bg-accent-hover transition-colors cursor-pointer"
              >
                <Pencil size={12} />
                {t('setup.edit')}
              </button>
            ) : (
              <>
                <button
                  type="button"
                  onClick={handleSave}
                  disabled={saving}
                  className="px-3 py-1.5 rounded-md text-xs font-medium bg-accent text-white
                    hover:bg-accent-hover disabled:opacity-50 transition-colors cursor-pointer"
                >
                  {saving ? t('setup.saving') : t('setup.save')}
                </button>
                <button
                  type="button"
                  onClick={handleCancel}
                  disabled={saving}
                  className="px-3 py-1.5 rounded-md text-xs font-medium text-text-secondary
                    border border-border-default hover:border-border-hover hover:text-text-primary disabled:opacity-50 transition-colors cursor-pointer"
                >
                  {t('common.cancel')}
                </button>
              </>
            )}
          </div>

          {readOnly && (!setup || !inventory || isSetupEmpty(setup)) ? (
            <div className="text-text-muted text-sm">{t('setup.noCurrentSetup')}</div>
          ) : inventory ? (
            <SetupForm
              data={readOnly ? (setup ?? {}) : data}
              inventory={inventory}
              readOnly={readOnly}
              onChange={update}
              onInventoryUpdate={setInventory}
            />
          ) : null}
        </>
      )}
    </div>
  );
}
